import {observer} from "mobx-react";
import {
    Box,
    Button,
    Card, CardActions,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle, IconButton
} from "@mui/material";
import React, {useEffect, useRef, useState} from "react";
import {useStore} from "../../stores/Store";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from '@mui/icons-material/Image';
import Accordion from "../../models/Accordion";
import Image from "../../models/Image";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

interface Props {
    accordion: Accordion
}

const cardStyle = {
    width: 350,
    m: 2,
    height: 350,
}

export default observer(function ImageManager({accordion}: Props){
    const [dialogIsOpen, setDialogIsOpen] = useState<boolean>(false);
    const { ImageStore, LanguageStore, AccordionStore } = useStore();
    const { currentLanguageMessages } = LanguageStore;
    const [images, setImages] = useState<Image[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const fileInputRef = useRef(null); // Reference to the hidden file input

    useEffect(() => {
        loadImages();
    }, []);

    const loadImages = async () => {
        setIsLoading(true);
        setImages(await ImageStore.loadImagesOfAccordion(accordion))
        setIsLoading(false);
    }

    const handleClose = () => setDialogIsOpen(false)
    const handleOpen = () => setDialogIsOpen(true);
    const handleAddImageClick = () => {
        //@ts-ignore
        fileInputRef.current.click(); // Trigger the hidden file input
    };
    const handleDeleteImageClick = async (id: number) => {
        setIsLoading(true);
        await ImageStore.delete(id, accordion.id);
        setImages(images.filter(i => i.id !== id));
        setIsLoading(false);
    }
    const handleSetAsThumbnailClick = async (imageId: number) => {
        setIsLoading(true);
        await AccordionStore.setAsThumbnail(accordion.id, imageId);
        setIsLoading(false);
    }
    const handleFileChange = async (event: any) => {
        const file = event.target.files[0];
        if (!file) {
            return;
        }

        setIsLoading(true);
        // FileReader to convert image to base64 string
        const reader = new FileReader();
        reader.onloadend = async () => {
            // reader.result contains the base64 string
            const base64String = reader.result as string;
            
            let extension = base64String.replace("data:image/", '');
            extension = extension.substring(0, extension.indexOf(";"));

            const imageEntity: Image = {
                id: 0, // Assuming id is assigned by the backend or ImageStore
                name: `${accordion.name}_${Date.now()}.${extension}`,
                data: base64String, // Directly use the base64 string
                accordions: [accordion]
            };
            // Create the image entity
            await ImageStore.create(imageEntity, accordion.id);
            await loadImages();
            setIsLoading(false);
        };
        reader.onerror = (error) => {
            console.error('Error converting image to Base64', error);
            setIsLoading(false);
        };
        // Read the file as a Data URL (base64 string)
        reader.readAsDataURL(file);
    };

    const renderLoadingScreen = () => isLoading &&
        <Box sx={{display: 'flex', justifyContent: 'center', width: '100%'}}>
            <CircularProgress color={'secondary'} />
        </Box>

    const renderImages = () => !isLoading && images.map(image => image &&
            <Card key={image.id} sx={cardStyle} variant={'outlined'}>
                <CardContent sx={{height: 260}}>
                    <img src={image.data} width={'100%'} />
                </CardContent>
                <CardActions sx={{justifyContent: 'center'}}>
                    <Button
                        startIcon={<ImageIcon />}
                        variant={'contained'}
                        color={'info'}
                        disabled={`thumb_${image.name}` === accordion.thumbnail?.name}
                        onClick={() => handleSetAsThumbnailClick(image.id)}
                    >
                        { currentLanguageMessages.AccordionsPage.ImageManager.setAsThumbnailButtonLabel }
                    </Button>
                    <Button
                        startIcon={<DeleteIcon />}
                        variant={'contained'}
                        color={'error'}
                        onClick={() => handleDeleteImageClick(image.id)}
                        sx={{marginTop: 'auto'}}
                    >
                        {currentLanguageMessages.GeneralComponentLabels.deleteButtonLabel}
                    </Button>
                </CardActions>
            </Card>
        )

    const renderCreateImage = () => !isLoading &&
        <Card
            sx={{...cardStyle, display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer'}}
            onClick={handleAddImageClick}
        >
            <AddIcon
                color={'success'}
                fontSize={'large'}
            />
            <input
                type="file"
                ref={fileInputRef}
                style={{display: 'none'}}
                onChange={handleFileChange}
                accept="image/*"
            />
        </Card>

    return (
        <>
            <Dialog open={dialogIsOpen} fullScreen>
                <DialogTitle sx={{display: 'flex', justifyContent: 'space-between'}}>
                    <Box>
                        {currentLanguageMessages.GeneralComponentLabels.manageImagesButtonLabel}
                    </Box>
                    <Box>
                        <IconButton
                            onClick={handleClose}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{display: 'flex', flexWrap: 'wrap'}}>
                    { renderLoadingScreen() }
                    { renderCreateImage() }
                    { renderImages() }
                </DialogContent>
                <DialogActions>
                    <Button
                        startIcon={<CloseIcon />}
                        onClick={handleClose}
                    >
                        {currentLanguageMessages.GeneralComponentLabels.closeButtonLabel}
                    </Button>
                </DialogActions>
            </Dialog>
            <Button
                variant={'contained'}
                startIcon={<ImageIcon />}
                onClick={handleOpen}
            >
                { currentLanguageMessages.GeneralComponentLabels.manageImagesButtonLabel }
            </Button>
        </>
    )
})